import React, { useCallback, useMemo, useState } from 'react';
import Loader from '../../common/Loader';
import {
    getPavWorkingConditionLabel,
    getSavedAssetApiId,
    getSavedAssetAttachments,
    getSavedAssetFields,
    getSavedAssetOwner,
    getSavedAssetSerialNumber,
    getSavedAssetTypeLabel,
} from './pavAssetUtils';

const VERIFIED_STATUSES = ['VERIFIED', 'APPROVED', 'ACCEPTED', 'PHYSICALLY_VERIFIED'];
const REJECTED_STATUSES = ['REJECTED', 'DECLINED'];

const readVerificationFlag = (value) => {
    if (value === true) return true;
    if (value === false) return false;
    const normalized = String(value ?? '').trim().toUpperCase();
    if (!normalized) return undefined;
    if (VERIFIED_STATUSES.includes(normalized)) return true;
    if (REJECTED_STATUSES.includes(normalized)) return false;
    return undefined;
};

// Only an explicit decision counts. `is_physically_verified: true` is a verified
// asset, and `pav_verification_data.is_verified` carries the recorded decision:
// `true` verified, `false` rejected, `null` still waiting. A plain
// `is_physically_verified: false` means nobody has decided yet, so it must never
// read as a rejection.
export const getSavedAssetVerification = (asset) => {
    const recordedDecision = asset?.pav_verification_data?.is_verified
        ?? asset?.asset?.pav_verification_data?.is_verified;
    const decision = readVerificationFlag(recordedDecision);
    if (decision !== undefined) return decision;

    if (asset?.is_physically_verified === true || asset?.asset?.is_physically_verified === true) return true;

    return readVerificationFlag(asset?.physical_verification_status)
        ?? readVerificationFlag(asset?.asset?.physical_verification_status);
};

const isPdfAttachment = (url) => /\.pdf(?:$|[?#])/i.test(String(url || ''));

// This project overrides Tailwind's height scale (`h-20` is 20rem here), so
// thumbnails are pinned with explicit values to stay square in every surface.
const TILE_CLASS = 'h-[6.4rem] w-[6.4rem]';
const COMPACT_TILE_CLASS = 'h-[5rem] w-[5rem]';
// Three cards to a row on every surface, and the three tracks stay put when the
// list is short, so a single search result keeps its card size instead of
// stretching across the whole width. Chat keeps a single column.
export const PAV_ASSET_GRID_CLASS = 'grid grid-cols-3 items-start gap-2';

const ATTACHMENT_BOX_CLASS = 'max-h-[15rem]';
const NARROW_ATTACHMENT_BOX_CLASS = 'max-h-[13rem]';
const COMPACT_ATTACHMENT_BOX_CLASS = 'max-h-[11rem]';

// An asset can carry many photos and a milestone many assets, so only a few
// thumbnails are mounted per card; the rest stay one click away in the media
// viewer. The mounted ones still load lazily, so scrolling decides what is
// fetched and decoded.
const VISIBLE_TILES = 6;
const NARROW_VISIBLE_TILES = 4;
const COMPACT_VISIBLE_TILES = 3;

const StatusPill = ({ isVerified }) => (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-font10 font-semibold ${isVerified ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-red-300 bg-red-50 text-red-700'}`}>
        <span className='material-icons text-font12'>{isVerified ? 'check_circle' : 'cancel'}</span>
        {isVerified ? 'Verified' : 'Rejected'}
    </span>
);

const AssetDetail = ({ label, value, isCompact }) => (
    <div className='min-w-0'>
        <p className={`truncate font-semibold uppercase tracking-wide text-slate-500 ${isCompact ? 'text-font09' : 'text-font09'}`} title={label}>{label}</p>
        <p className={`mt-0.5 break-words font-medium text-scogo21 ${isCompact ? 'text-font10' : 'text-font11'}`}>{String(value)}</p>
    </div>
);

const AttachmentTile = React.memo(({ attachment, index, isCompact, onOpen }) => {
    const [imageFailed, setImageFailed] = useState(false);
    const url = attachment.file_url || attachment.url;
    const name = attachment.name || attachment.file_name || `Attachment ${index + 1}`;
    const isPdf = isPdfAttachment(url);
    const showImage = !isPdf && !imageFailed;

    return (
        <button
            type='button'
            onClick={() => onOpen(index)}
            title={name}
            className={`group relative shrink-0 overflow-hidden rounded-md border border-scogoddd bg-white shadow-sm transition-transform hover:scale-105 ${isCompact ? COMPACT_TILE_CLASS : TILE_CLASS}`}
        >
            {showImage ? (
                <img
                    src={url}
                    alt={name}
                    loading='lazy'
                    decoding='async'
                    className='h-full w-full object-cover'
                    onError={() => setImageFailed(true)}
                />
            ) : (
                <span className={`flex h-full w-full flex-col items-center justify-center ${isPdf ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-700'}`}>
                    <span className='material-icons text-font18'>{isPdf ? 'picture_as_pdf' : 'insert_drive_file'}</span>
                    <span className='mt-0.5 text-font09 font-semibold uppercase'>{isPdf ? 'PDF' : 'File'}</span>
                </span>
            )}
            <span className='absolute inset-x-0 bottom-0 truncate bg-slate-900/70 px-1 py-0.5 text-font09 font-medium text-white'>{name}</span>
        </button>
    );
});

// One saved PAV asset: its details, its attachments as square thumbnails, and
// the physical verification decision. Shared by the authenticated verification modal and
// the added-assets view so both read identically.
function PavAssetReviewCard({
    asset,
    assetKey,
    milestoneId,
    isCompact = false,
    isNarrow = false,
    canVerify = false,
    outcome,
    pendingDecision,
    onVerify,
    onPreview,
    renderExtraActions,
    canEdit = false,
    isEditing = false,
    editDisabled = false,
    onEdit,
    editContent,
}) {
    // Saved assets arrive in many shapes and normalizing them walks several
    // fallbacks, so it is done once per asset instead of on every render.
    const { attachments, fields, owner, serialNumber, assetTypeLabel, assetId, savedVerification } = useMemo(() => ({
        attachments: getSavedAssetAttachments(asset),
        fields: getSavedAssetFields(asset),
        owner: getSavedAssetOwner(asset),
        serialNumber: getSavedAssetSerialNumber(asset),
        assetTypeLabel: getSavedAssetTypeLabel(asset),
        assetId: getSavedAssetApiId(asset),
        savedVerification: getSavedAssetVerification(asset),
    }), [asset]);
    const openAttachment = useCallback((index) => onPreview?.(attachments, index), [onPreview, attachments]);
    // A rejection has to say why, so the reject button opens a remark box first
    // and only the confirm inside it sends the decision.
    const [isRejecting, setIsRejecting] = useState(false);
    const [rejectRemark, setRejectRemark] = useState('');
    const [remarkError, setRemarkError] = useState('');
    const verification = outcome !== undefined ? outcome : savedVerification;
    const isDecided = verification !== undefined;
    const isSubmitting = pendingDecision !== undefined;
    const isVerifyDisabled = !assetId || isSubmitting;
    // A card sitting in a multi-column grid keeps two detail columns and a
    // shorter attachment box; only a full-width card spreads to four.
    const detailGridClass = isCompact || isNarrow ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4';
    const attachmentBoxClass = isCompact
        ? COMPACT_ATTACHMENT_BOX_CLASS
        : isNarrow ? NARROW_ATTACHMENT_BOX_CLASS : ATTACHMENT_BOX_CLASS;
    const visibleTileCount = isCompact ? COMPACT_VISIBLE_TILES : isNarrow ? NARROW_VISIBLE_TILES : VISIBLE_TILES;
    const hiddenTileCount = Math.max(attachments.length - visibleTileCount, 0);
    const extraActions = renderExtraActions?.(asset, assetKey);

    const closeRejectRemark = () => {
        setIsRejecting(false);
        setRejectRemark('');
        setRemarkError('');
    };

    const submitRejection = () => {
        const remark = rejectRemark.trim();
        if (!remark) {
            setRemarkError('Please add a remark explaining the rejection.');
            return;
        }
        setRemarkError('');
        setIsRejecting(false);
        onVerify?.({ assetKey, assetId, milestoneId, isVerified: false, remark });
    };

    return (
        <div className={`flex h-full flex-col rounded-lg border border-scogoddd bg-white shadow-sm ${isCompact ? 'p-2' : 'p-3'}`}>
            <div className='flex flex-wrap items-center gap-2 border-b border-scogoddd pb-2'>
                <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-scogolightblue text-scogoprimary'>
                    <span className='material-icons-outlined text-font14'>inventory_2</span>
                </span>
                <div className='min-w-0 flex-1'>
                    <p className={`truncate font-mono font-semibold text-scogo21 ${isCompact ? 'text-font11' : 'text-font12'}`} title={assetTypeLabel}>{assetTypeLabel}</p>
                </div>
                {isDecided && <StatusPill isVerified={verification} />}
                {canEdit && !isEditing && (
                    <button
                        type='button'
                        disabled={editDisabled || !assetId}
                        onClick={() => onEdit?.(assetKey)}
                        aria-label={`Edit ${assetTypeLabel}`}
                        title={!assetId ? 'This asset has no identifier, so it cannot be edited here.' : 'Edit asset fields'}
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-blue-200 bg-scogolightblue text-scogoprimary transition-colors ${editDisabled || !assetId ? 'cursor-not-allowed opacity-50' : 'hover:border-scogoprimary hover:bg-blue-100'}`}
                    >
                        <span className='material-icons-outlined text-font14'>edit</span>
                    </button>
                )}
            </div>

            {isEditing ? editContent : (fields.length > 0 || owner.name || owner.email) && (
                <div className={`mt-2 grid gap-x-4 gap-y-2 ${detailGridClass}`}>
                    {fields.map((field) => <AssetDetail key={field.key} label={field.label} value={getPavWorkingConditionLabel(field.key, field.value)} isCompact={isCompact} />)}
                    {owner.name && <AssetDetail label='Owner' value={owner.name} isCompact={isCompact} />}
                    {owner.email && <AssetDetail label='Owner Email' value={owner.email} isCompact={isCompact} />}
                </div>
            )}

            <div className='mt-2 flex-1 border-t border-scogoddd pt-2'>
                <div className='mb-1.5 flex items-center gap-2'>
                    <p className={`font-medium text-scogo21 ${isCompact ? 'text-font10' : 'text-font11'}`}>Attachments</p>
                    <span className='rounded-full bg-slate-100 px-2 py-0.5 text-font09 font-semibold text-slate-600'>{attachments.length}</span>
                </div>
                {attachments.length > 0 ? (
                    <div className={`flex flex-wrap gap-2 overflow-y-auto ${attachmentBoxClass}`}>
                        {attachments.slice(0, visibleTileCount).map((attachment, index) => (
                            <AttachmentTile
                                key={`${attachment.file_url || attachment.url}-${index}`}
                                attachment={attachment}
                                index={index}
                                isCompact={isCompact}
                                onOpen={openAttachment}
                            />
                        ))}
                        {hiddenTileCount > 0 && (
                            <button
                                type='button'
                                onClick={() => openAttachment(visibleTileCount)}
                                title={`View the remaining ${hiddenTileCount} attachment${hiddenTileCount === 1 ? '' : 's'}`}
                                className={`flex shrink-0 flex-col items-center justify-center rounded-md border border-scogoddd bg-scogof8 font-bold text-scogoprimary transition-colors hover:border-scogoprimary hover:bg-scogolightblue ${isCompact ? COMPACT_TILE_CLASS : TILE_CLASS}`}
                            >
                                <span className='text-font12'>+{hiddenTileCount}</span>
                                <span className='text-font09 font-normal text-slate-600'>more</span>
                            </button>
                        )}
                    </div>
                ) : (
                    <p className='rounded-md border border-dashed border-scogoddd bg-scogof8 px-3 py-2 text-center text-font10 font-normal text-slate-600'>No attachments were uploaded for this asset</p>
                )}
            </div>

            {((canVerify && !isEditing) || extraActions) && (
                <div className='mt-2 flex flex-wrap items-center gap-2 border-t border-scogoddd pt-2'>
                    {extraActions}
                    {canVerify && !isDecided && !assetId && (
                        <p className='text-font10 font-medium text-amber-800'>This asset has no identifier, so it cannot be verified here.</p>
                    )}
                    {canVerify && !isEditing && !isRejecting && (
                        <div className='ml-auto flex flex-wrap items-center gap-2'>
                            {isDecided ? (
                                <p className='text-font10 font-medium text-slate-600'>{verification ? 'Marked as physically verified.' : 'Marked as rejected.'}</p>
                            ) : (
                                <>
                                    <button
                                        type='button'
                                        disabled={isVerifyDisabled}
                                        onClick={() => { setRemarkError(''); setIsRejecting(true); }}
                                        className={`flex h-8 items-center gap-1.5 rounded-lg border border-scogoclosed bg-white px-3 text-font11 font-semibold text-scogoclosed transition-colors ${isVerifyDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-red-50'}`}
                                    >
                                        {pendingDecision === false ? <Loader size='12' thickness='2' color='#E74C3C' /> : <span className='material-icons text-font14'>close</span>}
                                        {pendingDecision === false ? 'Saving...' : 'Reject'}
                                    </button>
                                    <button
                                        type='button'
                                        disabled={isVerifyDisabled}
                                        onClick={() => onVerify?.({ assetKey, assetId, milestoneId, isVerified: true })}
                                        className={`flex h-8 items-center gap-1.5 rounded-lg bg-scogoprimary px-3 text-font11 font-semibold text-white shadow-sm transition-colors ${isVerifyDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-800'}`}
                                    >
                                        {pendingDecision === true ? <Loader size='12' thickness='2' color='#ffffff' /> : <span className='material-icons text-font14'>check</span>}
                                        {pendingDecision === true ? 'Saving...' : 'Approve'}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                    {canVerify && !isEditing && !isDecided && isRejecting && (
                        <div className='w-full rounded-lg border border-red-200 bg-red-50 p-2'>
                            <label htmlFor={`pav-reject-remark-${assetKey}`} className='text-font10 font-semibold text-red-800'>
                                Reason for rejection<span className='text-scogoclosed'> *</span>
                            </label>
                            <textarea
                                id={`pav-reject-remark-${assetKey}`}
                                rows={2}
                                autoFocus
                                value={rejectRemark}
                                disabled={isSubmitting}
                                onChange={(event) => {
                                    setRejectRemark(event.target.value);
                                    if (remarkError) setRemarkError('');
                                }}
                                placeholder='Tell the field team what is wrong with this asset'
                                className='mt-1 w-full resize-none rounded-md border border-scogoddd bg-white px-2 py-1.5 text-font11 font-normal text-scogo21 outline-none focus:border-scogoprimary'
                            />
                            {remarkError && <p className='mt-1 text-font10 font-medium text-scogoclosed'>{remarkError}</p>}
                            <div className='mt-1.5 flex flex-wrap items-center justify-end gap-2'>
                                <button
                                    type='button'
                                    disabled={isSubmitting}
                                    onClick={closeRejectRemark}
                                    className={`flex h-8 items-center rounded-lg border border-scogoddd bg-white px-3 text-font11 font-semibold text-slate-600 transition-colors ${isSubmitting ? 'cursor-not-allowed opacity-50' : 'hover:bg-slate-100'}`}
                                >
                                    Cancel
                                </button>
                                <button
                                    type='button'
                                    disabled={isVerifyDisabled}
                                    onClick={submitRejection}
                                    className={`flex h-8 items-center gap-1.5 rounded-lg bg-scogoclosed px-3 text-font11 font-semibold text-white shadow-sm transition-colors ${isVerifyDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-red-700'}`}
                                >
                                    <span className='material-icons text-font14'>close</span>
                                    Confirm Reject
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// A milestone can hold many assets, so a decision or an upload on one card must
// not re-render the thumbnails of every other card.
export default React.memo(PavAssetReviewCard);
